import React from 'react';
import BreadcrumbCustom from '../../../Component/breadcrumb/BreadcrumbCustom';
import AlertMessage from '../../../Component/alert/AlertMessage';
import { FormErrors } from '../../../Component/SubmissionStatus';
import BaseScreen from '../BaseScreen';
import { SCREENS } from '../../../constants/screens.constant';
import { UserService } from '../../../services/service.users';
import { StyledText } from '../../../Component/StyledText';
import i18n from 'i18next';
import LoadingOverlay from '../../../Component/loader/LoadingOverlay';

/**
 * change password component.
 */
class ChangePassword extends BaseScreen {
  constructor (props) {
    super(props);

    let isAuthenticated = true;

    if (!this.isAuth()) {
      isAuthenticated = false;
      this.goToScreen(SCREENS.login);
    }

    this.state = {
      isAuthenticated: isAuthenticated,
      currentPasswordFieldType: 'password',
      newPasswordFieldType: 'password',
      confirmPasswordFieldType: 'password',
      formFields: {
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      },
      formErrors: {
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      },
      formValid: false,

      // for alert messages
      alert: {
        type: null,
        message: null
      },

      isWaitingApiResponse: false
    };
  }

  /**
   * go back to previous page.
   */
  goBack () {
    this.props.history.goBack();
  }

  /**
   * reset the form to previous state
   */
  resetFormFields () {
    this.setState({
      formFields: {
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      },
      formErrors: {
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      },
      formValid: false
    });
  }

  /**
   * submit handler when user press the save button.
   * @param {*} event
   */
  submitHandler (event) {
    event.preventDefault();

    // api request/response
    const currentPassword = this.state.formFields.currentPassword;
    const newPassword = this.state.formFields.newPassword;
    this.setState({
      isWaitingApiResponse: true
    });
    UserService.changePassword(currentPassword, newPassword).then(
      response => {
        // handle success message
        this.setState({
          alert: {
            type: 'success',
            message: i18n.t('home.alert.profileUpdateSuccess')
          },
          isWaitingApiResponse: false
        });
      }, error => {
        // handle error message
        let alertMsg = i18n.t('common.genericApiError');
        if (error && error.data && error.data.message) {
          alertMsg = error.data.message;
        }
        this.setState({
          alert: {
            type: 'danger',
            message: alertMsg
          },
          isWaitingApiResponse: false
        });
      });
  }

  /*
   alert message handling
   */
  handleAlertDismiss () {
    this.setState({
      alert: {
        type: null,
        message: null
      }
    });
  }

  /**
   * toggle field from normal text to encrypted type.
   * @param {*} fieldName
   * @param {*} event
   */
  toggleFieldType (fieldName, event) {
    event.preventDefault();

    if (fieldName === 'currentPassword') {
      this.setState({ currentPasswordFieldType: this.state.currentPasswordFieldType === 'password' ? 'text' : 'password' });
    } else if (fieldName === 'newPassword') {
      this.setState({ newPasswordFieldType: this.state.newPasswordFieldType === 'password' ? 'text' : 'password' });
    } else if (fieldName === 'confirmPassword') {
      this.setState({ confirmPasswordFieldType: this.state.confirmPasswordFieldType === 'password' ? 'text' : 'password' });
    }
  }

  /**
   * handle user input for validation purpose.
   * @param {*} event
   */
  handleUserInput (event) {
    event.preventDefault();

    const name = event.target.name;
    const value = event.target.value;

    const formFields = this.state.formFields;
    formFields[name] = value;

    const fieldValidationErrors = { ...this.state.formErrors };

    const newPwd = formFields.newPassword;
    const cnfPwd = formFields.confirmPassword;

    let msg = '';

    switch (name) {
      case 'currentPassword':
      case 'newPassword':
      case 'confirmPassword':
        if (value.length === 0) {
          msg = i18n.t('home.alert.required');
        } else if (value.length < 8) {
          msg = i18n.t('home.alert.passwordMin');
        } else if (name === 'newPassword' && cnfPwd) {
          // handle password mismatch
          if (cnfPwd !== value) {
            msg = i18n.t('home.alert.passConfirmPassNoMatch');
          } else {
            fieldValidationErrors.confirmPassword = '';
          }
        } else if (name === 'confirmPassword' && newPwd) {
          // handle password mismatch
          if (newPwd !== value) {
            msg = i18n.t('home.alert.passConfirmPassNoMatch');
          } else {
            fieldValidationErrors.newPassword = '';
          }
        }

        fieldValidationErrors[name] = msg;
        break;

      default:
        break;
    }

    const isFormValid = (formFields.currentPassword && formFields.newPassword && formFields.confirmPassword) &&
      (fieldValidationErrors.currentPassword === '' && fieldValidationErrors.newPassword === '' &&
        fieldValidationErrors.confirmPassword === '');

    this.setState({
      formFields: formFields,
      formErrors: fieldValidationErrors,
      formValid: isFormValid
    });
  }

  /**
   * react render function to render change password form
   */
  render () {
    const breadcrumb = [
      { id: 'home', displayName: i18n.t('home.breadcrumb.home'), href: '#/home', className: '', link: true },
      { id: 'change-password', displayName: i18n.t('home.breadcrumb.changePass'), className: '', link: false }
    ];

    return (
      <div className="container-fluid p-4">
        <BreadcrumbCustom breadcrumb={breadcrumb} />

        <div className="clearfix"></div>
        <div>
          {
            this.state.alert.message &&
            <AlertMessage message={this.state.alert.message}
              type={this.state.alert.type}
              isAlertOpen={!!(this.state.alert.message)}
              handleDismiss={this.handleAlertDismiss.bind(this)}
            />
          }

          <LoadingOverlay active={this.state.isWaitingApiResponse} text="Your new password is being processed.  This may take a moment">
            <div className=" change-password">
              <form>
                <h2 data-testid="change-pwd-heading">
                  {i18n.t('home.changePass')}
                </h2>
                <div className="row">
                  <div className="view-profile-details">
                    <div className="col-md-8 margin-auto">
                      <div className="form-group">
                        <div className="input-group">
                          <label htmlFor="currentPassword" data-testid="change-pwd-label-current-pwd">
                            {i18n.t('home.form.label.currentPassword')} <StyledText uiText="*"/>
                          </label>
                          <input type={this.state.currentPasswordFieldType}
                            required
                            className="form-control"
                            name="currentPassword" placeholder={i18n.t('home.form.placeholder.currentPassword')}
                            value={this.state.formFields.currentPassword}
                            onChange={this.handleUserInput.bind(this)} minLength="8" 
                            data-testid="change-pwd-input-current-pwd" />
                          <button className="btn input-group-addon" onClick={this.toggleFieldType.bind(this, 'currentPassword')}>
                            <i className={'fa ' + (this.state.currentPasswordFieldType === 'password' ? 'fa-eye-slash' : 'fa-eye')}></i>
                          </button>
                        </div>
                        <FormErrors formErrors={this.state.formErrors.currentPassword} 
                          testId="current-pwd"/>
                      </div>
                      <div className="hr-line-dashed"></div>
                      <div className="form-group">
                        <div className="input-group">
                          <label htmlFor="newPassword"> {i18n.t('home.form.label.newPassword')} <StyledText uiText="*"/></label>
                          <input type={this.state.newPasswordFieldType}
                            required
                            className="form-control"
                            name="newPassword" placeholder={i18n.t('home.form.placeholder.newPassword')}
                            value={this.state.formFields.newPassword}
                            onChange={this.handleUserInput.bind(this)} minLength="8" 
                            data-testid="change-pwd-input-new-pwd"/>
                          <button className="btn input-group-addon" onClick={this.toggleFieldType.bind(this, 'newPassword')}>
                            <i className={'fa ' + (this.state.newPasswordFieldType === 'password' ? 'fa-eye-slash' : 'fa-eye')}></i>
                          </button>
                        </div>
                        <FormErrors formErrors={this.state.formErrors.newPassword} />
                      </div>
                      <div className="hr-line-dashed "></div>
                      <div className="form-group">
                        <div className="input-group">
                          <label htmlFor="confirmPassword"> {i18n.t('home.form.label.confirmPassword')} <StyledText uiText="*"/></label>
                          <input type={this.state.confirmPasswordFieldType}
                            required
                            className="form-control"
                            name="confirmPassword" placeholder={i18n.t('home.form.placeholder.confirmPassword')}
                            value={this.state.formFields.confirmPassword}
                            onChange={this.handleUserInput.bind(this)} minLength="8" 
                            data-testid="change-pwd-input-confirm-pwd"/>
                          <button className="btn input-group-addon" onClick={this.toggleFieldType.bind(this, 'confirmPassword')}>
                            <i className={'fa ' + (this.state.confirmPasswordFieldType === 'password' ? 'fa-eye-slash' : 'fa-eye')}></i>
                          </button>
                        </div>
                        <FormErrors formErrors={this.state.formErrors.confirmPassword} 
                          testId="confirm-pwd"/>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-12 text-right f-btn">
                    <button type="button" className="btn btn-secondary mr-2"
                      onClick={this.resetFormFields.bind(this)} 
                      data-testid="change-pwd-btn-reset">
                      {i18n.t('home.form.button.reset')}
                    </button>
                    <button type="button" className="btn btn-secondary mr-2"
                      onClick={this.goBack.bind(this)} 
                      data-testid="change-pwd-btn-back">
                      {i18n.t('home.form.button.back')}
                    </button>
                    <button type="submit" className="btn btn-primary"
                      disabled={(!this.state.formValid || this.state.isWaitingApiResponse)}
                      onClick={this.submitHandler.bind(this)} 
                      data-testid="change-pwd-btn-save">
                      {i18n.t('home.form.button.save')}
                    </button>
                  </div>

                </div>
              </form>
            </div>
          </LoadingOverlay>
        </div>
      </div>
    );
  }
}

export default ChangePassword;
